import React, {Fragment, useState} from "react"
import momentTZ from "moment-timezone"
import {
  useListCustomRewardsTransactionsQuery,
  useMyListCustomRewardsTransactionsQuery,
  useRedeemCustomRewardMutation
} from "@irati/custom-reward/hooks"
import {CustomReward, CustomRewardTransaction, ListCustomRewardsTransactionsFilter} from "@irati/types"
import Spacing from "@irati/ui/src/Spacing"
import groupWalletItemsByDate from "@irati/wallet/helpers/groupWalletItemsByDate"
import CustomRewardTransactionItem from "../StatementItems/StatementItem/CustomReward"
import NoData from "../NoData"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import isMobileDevice from "@irati/utils/mobile"
import Loading from "@irati/ui/src/Loading"
import DateHeader from "../StatementItems/DateHeader"
import GenericModal from "@irati/ui/src/GenericModal"
import {Body} from "@irati/ui/src/Typography"
import {Hr} from "@irati/ui/src/Layout"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import {ValidationButton} from "@irati/ui/src/FormItem"
import Error from "@irati/ui/src/Loading/Error"

interface Props {
  filters?: ListCustomRewardsTransactionsFilter
  isUser?: boolean
  isRedeemable?: boolean
}
const CustomRewardsTransactions = (props: Props) => {
  const {formatMessage} = useIntl()
  const {filters, isUser, isRedeemable} = props
  const [showRedemptionDetails, setShowRedemptionDetails] = useState<CustomReward>()
  const [redeemCustomReward, apollo] = useRedeemCustomRewardMutation()
  const [customRewardToRedeem, setCustomRewardToRedeem] = useState<CustomRewardTransaction>()
  const {data: results, loading} = isUser
    ? useMyListCustomRewardsTransactionsQuery(null)
    : useListCustomRewardsTransactionsQuery(
        {
          filters
        },
        {
          fetchPolicy: "cache-and-network"
        }
      )
  const groupedData = groupWalletItemsByDate([], results || [])
  const onRedeemCustomReward = () => {
    redeemCustomReward({
      variables: {
        uuid: customRewardToRedeem?.uuid
      }
    }).then(() => {
      setCustomRewardToRedeem(null)
    })
  }
  return (
    <Loading isLoading={loading}>
      <Spacing marginTop={2}>
        {groupedData.results.length === 0 && (
          <NoData useCardStyle={isMobileDevice()} text={formatMessage({id: "platform.noStatementItems.NoCustomRewardsText"})}></NoData>
        )}
        {groupedData.results.map(({data, date}, i) => {
          const key = date.format("YYYY-MM-DD")
          return (
            <Fragment key={key}>
              <Spacing marginTop={3} marginBottom={3}>
                <DateHeader date={date} />
              </Spacing>
              {data.map((item: CustomRewardTransaction) => (
                <CustomRewardTransactionItem
                  item={item}
                  key={item.uuid}
                  isRedeemable={isRedeemable}
                  isUser={isUser}
                  handleShowRedemptionDetails={setShowRedemptionDetails}
                  handleRedeemCustomReward={setCustomRewardToRedeem}
                />
              ))}
            </Fragment>
          )
        })}
      </Spacing>
      <GenericModal
        show={!!showRedemptionDetails}
        title={showRedemptionDetails?.title}
        onHide={() => setShowRedemptionDetails(null)}
        hideActions>
        <Body>{showRedemptionDetails?.redemptionInstructions}</Body>
      </GenericModal>
      <GenericModal
        onHide={() => setCustomRewardToRedeem(null)}
        show={!!customRewardToRedeem}
        title={formatMessage({id: "platform.customRewards.redeem.modalTitle"})}
        hideActions>
        <Body>
          <FormattedMessage id="platform.customRewards.redeem.modalBody" />
        </Body>
        <Hr />
        <Flex justifyContent="space-between">
          <Body>{customRewardToRedeem?.customReward.title}</Body>
          <Body>{momentTZ().format("DD/MMMM/YYYY")}</Body>
        </Flex>
        <Hr />
        <Flex direction="column" gap={2}>
          <ValidationButton full primary onClick={onRedeemCustomReward} apolloState={apollo}>
            <FormattedMessage id="platform.customRewards.redeem.modal.cta.confirm" />
          </ValidationButton>
          <Flex justifyContent="center">
            <Button onClick={() => setCustomRewardToRedeem(null)} tertiary>
              <FormattedMessage id="platform.customRewards.redeem.modal.cta.cancel" />
            </Button>
          </Flex>
          {apollo.error && (
            <Spacing marginTop={2}>
              <Error errorMsg={apollo.errorMsg} error={apollo.error} />
            </Spacing>
          )}
        </Flex>
      </GenericModal>
    </Loading>
  )
}

export default CustomRewardsTransactions
